import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Header } from 'components/common/header/Header';
import { MeetingRepository } from 'repository/meetingRepository';
import './MyMeetingsList.css';

type MeetingFilter = 'all' | 'upcoming' | 'past';

const PAGE_SIZE = 10;

interface Meeting {
    id: string;  // API는 문자열 ID를 반환
    name: string;
    date: string;
    time: string;
    location: string;
    host: string;
    attendingCount: number;   // 참석 확정
    undecidedCount: number;   // 대기중
    status: 'upcoming' | 'ongoing' | 'completed';
    scheduleEndDateTime?: string;  // 종료 시간 (지난 모임 판단용)
    scheduleStartDateTime?: string;  // 시작 시간 (정렬용)
}

// API 응답을 Meeting 인터페이스로 변환하는 함수
const transformMeeting = (meeting: any): Meeting => {
    const datetime = meeting.schedule?.startDatetime || meeting.meetingDatetime;
    const startDatetime = meeting.schedule?.startDatetime;
    const endDatetime = meeting.schedule?.endDatetime;
    const now = new Date();
    let status: 'upcoming' | 'ongoing' | 'completed' = 'upcoming';

    if (endDatetime && new Date(endDatetime) < now) {
        status = 'completed';
    } else if (startDatetime && new Date(startDatetime) <= now) {
        status = 'ongoing';
    } else {
        status = 'upcoming';
    }

    return {
        id: meeting.meetingId,
        name: meeting.meetingName,
        date: datetime ? new Date(datetime).toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short'
        }) : '일정 미정',
        time: datetime ? new Date(datetime).toLocaleTimeString('ko-KR', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }) : '',
        location: meeting.place
            ? (meeting.place.placeName && meeting.place.placeAddress
                ? `${meeting.place.placeName} (${meeting.place.placeAddress})`
                : meeting.place.placeAddress || meeting.place.placeName || '장소 미정')
            : '장소 미정',
        host: meeting.creatorNickname || '알 수 없음',
        attendingCount: meeting.attendingCount || 0,
        undecidedCount: meeting.undecidedCount || 0,
        status,
        scheduleEndDateTime: endDatetime,
        scheduleStartDateTime: datetime
    };
};

export const MyMeetingsList = () => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<MeetingFilter>('all');
    const [meetings, setMeetings] = useState<Meeting[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);

    // API에서 모임 목록 불러오기
    const fetchMeetings = useCallback(async (pageNum: number, isInitial: boolean = false) => {
        try {
            if (isInitial) {
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }

            const response = await MeetingRepository.onGetMyMeetings({
                page: pageNum,
                pageSize: PAGE_SIZE
            });

            if (response?.data) {
                const meetingList = response.data.data?.list || response.data.list || response.data.data || [];
                const total = response.data.data?.totalCount || response.data.totalCount || 0;
                const transformedMeetings = meetingList.map(transformMeeting);

                setTotalCount(total);

                if (isInitial) {
                    setMeetings(transformedMeetings);
                } else {
                    setMeetings(prev => [...prev, ...transformedMeetings]);
                }

                // 더 불러올 데이터가 있는지 확인
                setHasMore(meetingList.length === PAGE_SIZE);
            }
        } catch (error) {
            console.error('모임 목록 조회 실패:', error);
            if (isInitial) {
                setMeetings([]);
            }
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, []);

    // 초기 로딩
    useEffect(() => {
        fetchMeetings(1, true);
    }, [fetchMeetings]);

    // 무한 스크롤 Observer 설정
    useEffect(() => {
        if (isLoading || isLoadingMore || !hasMore) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
                    setPage(prev => prev + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observerRef.current.observe(loadMoreRef.current);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [isLoading, isLoadingMore, hasMore]);

    // 페이지 변경 시 추가 데이터 로드
    useEffect(() => {
        if (page > 1) {
            fetchMeetings(page, false);
        }
    }, [page, fetchMeetings]);

    // 정렬 함수: 종료된 모임은 뒤로, 가까운 일정부터 표시
    const sortMeetings = (meetingList: Meeting[]) => {
        return [...meetingList].sort((a, b) => {
            // 1. 종료된 모임은 뒤로
            if (a.status === 'completed' && b.status !== 'completed') return 1;
            if (a.status !== 'completed' && b.status === 'completed') return -1;

            // 2. 일정이 없는 모임은 뒤로
            if (!a.scheduleStartDateTime && b.scheduleStartDateTime) return 1;
            if (a.scheduleStartDateTime && !b.scheduleStartDateTime) return -1;
            if (!a.scheduleStartDateTime && !b.scheduleStartDateTime) return 0;

            // 3. 날짜 기준 정렬
            const dateA = new Date(a.scheduleStartDateTime!).getTime();
            const dateB = new Date(b.scheduleStartDateTime!).getTime();

            if (a.status === 'completed' && b.status === 'completed') {
                // 종료된 모임은 최근 것부터 (내림차순)
                return dateB - dateA;
            }
            // 예정/진행 중인 모임은 가까운 것부터 (오름차순)
            return dateA - dateB;
        });
    };

    const filteredMeetings = sortMeetings(meetings.filter(meeting => {
        if (filter === 'upcoming') return meeting.status === 'upcoming' || meeting.status === 'ongoing';
        if (filter === 'past') return meeting.status === 'completed';
        return true;
    }));

    const getStatusBadge = (status: Meeting['status']) => {
        switch (status) {
            case 'upcoming':
                return { text: '예정', className: 'upcoming' };
            case 'ongoing':
                return { text: '진행중', className: 'ongoing' };
            case 'completed':
                return { text: '종료', className: 'completed' };
        }
    };

    const handleMeetingClick = (meetingId: string) => {
        navigate(`/meeting/detail/${meetingId}`);
    };

    return (
        <div className="my-meetings-wrapper">
            <Header title="나의 모임" isSubPage />

            <div className="my-meetings-content">
                {/* Filter Tabs */}
                <div className="filter-tabs">
                    <button
                        className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        전체 ({meetings.length})
                    </button>
                    <button
                        className={`filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setFilter('upcoming')}
                    >
                        예정 ({meetings.filter(m => m.status === 'upcoming' || m.status === 'ongoing').length})
                    </button>
                    <button
                        className={`filter-tab ${filter === 'past' ? 'active' : ''}`}
                        onClick={() => setFilter('past')}
                    >
                        지난 모임 ({meetings.filter(m => m.status === 'completed').length})
                    </button>
                </div>

                {/* Meetings List */}
                {isLoading ? (
                    <div className="empty-state">
                        <div className="empty-icon">⏳</div>
                        <div className="empty-title">로딩 중...</div>
                    </div>
                ) : filteredMeetings.length > 0 ? (
                    <div className="meetings-list">
                        {filteredMeetings.map(meeting => {
                            const badge = getStatusBadge(meeting.status);
                            return (
                                <div
                                    key={meeting.id}
                                    className="meeting-card"
                                    onClick={() => handleMeetingClick(meeting.id)}
                                >
                                    <div className="meeting-card-header">
                                        <h3 className="meeting-card-name">{meeting.name}</h3>
                                        <span className={`status-badge ${badge.className}`}>
                                            {badge.text}
                                        </span>
                                    </div>

                                    <div className="meeting-card-info">
                                        <div className="info-item">
                                            <span className="info-icon">📅</span>
                                            <span className="info-text">{meeting.date}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-icon">⏰</span>
                                            <span className="info-text">{meeting.time}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-icon">📍</span>
                                            <span className="info-text">{meeting.location}</span>
                                        </div>
                                    </div>

                                    <div className="meeting-card-footer">
                                        <div className="host-info">
                                            <span className="host-label">주최:</span>
                                            <span className="host-name">{meeting.host}</span>
                                        </div>
                                        <div className="participants-info">
                                            <span className="attending-count">✓ {meeting.attendingCount}명</span>
                                            {meeting.undecidedCount > 0 && (
                                                <span className="undecided-count">· 대기 {meeting.undecidedCount}명</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* 무한 스크롤 트리거 */}
                        <div ref={loadMoreRef} className="load-more-trigger">
                            {isLoadingMore && (
                                <div className="loading-more">
                                    <span className="loading-spinner">⏳</span>
                                    <span>불러오는 중...</span>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-icon">📭</div>
                        <div className="empty-title">
                            {filter === 'upcoming' && '예정된 모임이 없어요'}
                            {filter === 'past' && '지난 모임이 없어요'}
                            {filter === 'all' && '참여 중인 모임이 없어요'}
                        </div>
                        <div className="empty-subtitle">
                            새로운 모임을 만들거나<br />초대 코드로 참여해 보세요!
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
